import type { DocIdentifier } from '@/types/external/2work/api/common/DocIdentifier.ts'
import type { DocDeletionBulkRequest } from '@/types/external/2work/api/request/DocDeletionBulkRequest.ts'
import type { Address } from '@/types/external/2work/doctypes/generatorOutput/Contacts/Address.ts'
import type { ActionMapEntry, AlertActionContext } from '@/types/internal/frappeActionTypes.ts'

import SingleAddress from '@/components/addresses/SingleAddress.vue'
import BankAccountCreateModal from '@/components/bank/BankAccountCreateModal.vue'
import { doctypeCreateSlideInMap } from '@/constants/doctypeCreateSlideInMap.ts'
import { AlertType } from '@/constants/ui'
import { useReportIframeStore } from '@/iframeStores/reportIframeStore.ts'
import { useAlertManagerStore } from '@/stores/alertManager.ts'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI.ts'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore.ts'
import { stripHtml } from '@/utilities/htmlHelpers.ts'
import { translationFunctionDefault } from '@/utilities/translations.ts'

type DefaultTranslationFunction = ReturnType<typeof translationFunctionDefault>

type DisabledStatusPersistHandler = (docName: string, disabled: 0 | 1) => Promise<unknown>

type DisabledStatusUpdateHandler = (
  docName: string,
  disabled: 0 | 1,
  successMessage: string,
  errorMessage: string,
) => Promise<void>

interface DisabledStatusActionConfig {
  activateActionName: string
  deactivateActionName: string
  translationBase: string
  updateDisabledStatus: DisabledStatusUpdateHandler
}

interface DeleteDocumentActionConfig {
  deletionTranslationBase: string
  doctype: string
  statusTranslationBase: string
  updateDisabledStatus: DisabledStatusUpdateHandler
  refreshAction?: 'refreshDoc' | 'reloadDoc'
}

interface UpdateDocumentDisabledStatusOptions {
  disabled: 0 | 1
  docName: string
  errorMessage: string
  persistDisabledStatus: DisabledStatusPersistHandler
  successMessage: string
  refreshAction?: 'refreshDoc' | 'reloadDoc'
}

const translationKey = (translationBase: string, key: string): string => {
  return `frappeInterceptNewCreateActionMap.${translationBase}.${key}`
}

const isDisableInsteadOfDeleteMessage = (reason: string): boolean => {
  const normalizedReason = reason.toLowerCase()

  return (
    normalizedReason.includes('instead of deleting') ||
    (normalizedReason.includes('deaktivieren') && normalizedReason.includes('löschen'))
  )
}

const getDeletionBlockedMessage = (
  tfDefault: DefaultTranslationFunction,
  reason: string,
  config: DeleteDocumentActionConfig,
): string => {
  const normalizedReason = stripHtml(reason).trim()

  if (isDisableInsteadOfDeleteMessage(normalizedReason)) {
    return tfDefault(translationKey(config.deletionTranslationBase, 'cannotDeleteLinkedMessage'))
  }

  return tfDefault(translationKey(config.deletionTranslationBase, 'cannotDeleteMessage'), {
    reason: normalizedReason || reason,
  })
}

const updateDocumentDisabledStatus = async ({
  disabled,
  docName,
  errorMessage,
  persistDisabledStatus,
  refreshAction = 'refreshDoc',
  successMessage,
}: UpdateDocumentDisabledStatusOptions): Promise<void> => {
  const simpleNotificationStore = useSimpleNotificationStore()
  const reportIframeStore = useReportIframeStore()

  try {
    await persistDisabledStatus(docName, disabled)
    reportIframeStore.triggerIframeAction(refreshAction, {})
    simpleNotificationStore.triggerSimpleNotificationSuccess(successMessage)
  } catch (errorObj: unknown) {
    console.debug(errorObj)
    simpleNotificationStore.triggerSimpleNotificationError(errorMessage)
    throw errorObj
  }
}

const updateBankAccountDisabledStatus = async (
  docName: string,
  disabled: 0 | 1,
  successMessage: string,
  errorMessage: string,
): Promise<void> => {
  const frappeNestCustomAPIStore = useFrappeNestCustomAPIStore()

  await updateDocumentDisabledStatus({
    disabled,
    docName,
    errorMessage,
    persistDisabledStatus: async (targetDocName, targetDisabled) => {
      return await frappeNestCustomAPIStore.updateBankAccount(targetDocName, {
        disabled: targetDisabled,
        ...(targetDisabled === 1 ? { is_default: 0 as const } : {}),
      })
    },
    successMessage,
  })
}

const updateAddressDisabledStatus = async (
  docName: string,
  disabled: 0 | 1,
  successMessage: string,
  errorMessage: string,
): Promise<void> => {
  const frappeNestCustomAPIStore = useFrappeNestCustomAPIStore()

  await updateDocumentDisabledStatus({
    disabled,
    docName,
    errorMessage,
    persistDisabledStatus: async (targetDocName, targetDisabled) => {
      const addressPayload: Partial<Address> = { disabled: targetDisabled }

      if (targetDisabled === 1) {
        addressPayload.is_primary_address = 0
        addressPayload.is_shipping_address = 0
      }

      return await frappeNestCustomAPIStore.updateAddress(targetDocName, addressPayload)
    },
    refreshAction: 'reloadDoc',
    successMessage,
  })
}

const openDeletionBlockedAlert = (
  tfDefault: DefaultTranslationFunction,
  docName: string,
  reason: string,
  config: DeleteDocumentActionConfig,
  context?: AlertActionContext,
): void => {
  const alertManagerStore = useAlertManagerStore()
  const targetAlreadyDisabled = context?.targetDisabled === true
  const cancelLabelKey = targetAlreadyDisabled ? 'closeLabel' : 'cancelLabel'

  window.setTimeout(() => {
    alertManagerStore.openAlert({
      alertType: AlertType.danger,
      cancelLabel: tfDefault(translationKey(config.deletionTranslationBase, cancelLabelKey)),
      ...(targetAlreadyDisabled
        ? {}
        : {
            ctaLabel: tfDefault(
              translationKey(config.deletionTranslationBase, 'deactivateCtaLabel'),
            ),
            onSubmit: async (): Promise<void> => {
              await config.updateDisabledStatus(
                docName,
                1,
                tfDefault(translationKey(config.statusTranslationBase, 'deactivateSuccessMsg')),
                tfDefault(translationKey(config.statusTranslationBase, 'deactivateErrorMsg')),
              )
            },
          }),
      message: getDeletionBlockedMessage(tfDefault, reason, config),
      messageTitle: tfDefault(translationKey(config.deletionTranslationBase, 'cannotDeleteTitle')),
    })
  }, 0)
}

const getDisabledStatusActions = (
  tfDefault: DefaultTranslationFunction,
  config: DisabledStatusActionConfig,
): Record<string, ActionMapEntry> => {
  return {
    [config.activateActionName]: {
      alertType: AlertType.warning,
      cancelLabel: tfDefault(translationKey(config.translationBase, 'cancelLabel')),
      ctaLabel: tfDefault(translationKey(config.translationBase, 'activateCtaLabel')),
      message: tfDefault(translationKey(config.translationBase, 'activateMessage')),
      messageTitle: tfDefault(translationKey(config.translationBase, 'activateTitle')),
      onSubmit: async (docName: string): Promise<void> => {
        await config.updateDisabledStatus(
          docName,
          0,
          tfDefault(translationKey(config.translationBase, 'activateSuccessMsg')),
          tfDefault(translationKey(config.translationBase, 'activateErrorMsg')),
        )
      },
      type: 'alert',
    },
    [config.deactivateActionName]: {
      alertType: AlertType.warning,
      cancelLabel: tfDefault(translationKey(config.translationBase, 'cancelLabel')),
      ctaLabel: tfDefault(translationKey(config.translationBase, 'deactivateCtaLabel')),
      message: tfDefault(translationKey(config.translationBase, 'deactivateMessage')),
      messageTitle: tfDefault(translationKey(config.translationBase, 'deactivateTitle')),
      onSubmit: async (docName: string): Promise<void> => {
        await config.updateDisabledStatus(
          docName,
          1,
          tfDefault(translationKey(config.translationBase, 'deactivateSuccessMsg')),
          tfDefault(translationKey(config.translationBase, 'deactivateErrorMsg')),
        )
      },
      type: 'alert',
    },
  }
}

const getDeleteDocumentAction = (
  tfDefault: DefaultTranslationFunction,
  config: DeleteDocumentActionConfig,
): ActionMapEntry => {
  return {
    alertType: AlertType.warning,
    cancelLabel: tfDefault(translationKey(config.deletionTranslationBase, 'cancelLabel')),
    ctaLabel: tfDefault(translationKey(config.deletionTranslationBase, 'ctaLabel')),
    message: tfDefault(translationKey(config.deletionTranslationBase, 'message')),
    messageTitle: tfDefault(translationKey(config.deletionTranslationBase, 'title')),
    onSubmit: async (docName: string, context?: AlertActionContext): Promise<void> => {
      const frappeNestCustomAPIStore = useFrappeNestCustomAPIStore()
      const simpleNotificationStore = useSimpleNotificationStore()
      const reportIframeStore = useReportIframeStore()
      const documents: DocIdentifier[] = [{ doctype: config.doctype, name: docName }]
      const docDeletionBulkRequest: DocDeletionBulkRequest = { documents }

      try {
        const response = await frappeNestCustomAPIStore.deleteDocuments(
          docDeletionBulkRequest,
          true,
        )

        if (response && typeof response === 'object' && 'errorMessage' in response) {
          openDeletionBlockedAlert(tfDefault, docName, response.errorMessage, config, context)
          return
        }

        reportIframeStore.triggerIframeAction(config.refreshAction ?? 'refreshDoc', {})
        simpleNotificationStore.triggerSimpleNotificationSuccess(
          tfDefault(translationKey(config.deletionTranslationBase, 'successMsg')),
        )
      } catch (errorObj: unknown) {
        console.debug(errorObj)
        simpleNotificationStore.triggerSimpleNotificationError(
          tfDefault(translationKey(config.deletionTranslationBase, 'errorMsg')),
        )
      }
    },
    type: 'alert',
  }
}

const getBankAccountActions = (
  tfDefault: DefaultTranslationFunction,
): Record<string, ActionMapEntry> => {
  return {
    ...getDisabledStatusActions(tfDefault, {
      activateActionName: 'activate_bank_account',
      deactivateActionName: 'deactivate_bank_account',
      translationBase: 'bankAccountStatus',
      updateDisabledStatus: updateBankAccountDisabledStatus,
    }),
    delete_bank_account: getDeleteDocumentAction(tfDefault, {
      deletionTranslationBase: 'bankAccountDeletion',
      doctype: 'Bank Account',
      statusTranslationBase: 'bankAccountStatus',
      updateDisabledStatus: updateBankAccountDisabledStatus,
    }),
  }
}

const getAddressActions = (
  tfDefault: DefaultTranslationFunction,
): Record<string, ActionMapEntry> => {
  return {
    ...getDisabledStatusActions(tfDefault, {
      activateActionName: 'activate_address',
      deactivateActionName: 'deactivate_address',
      translationBase: 'addressStatus',
      updateDisabledStatus: updateAddressDisabledStatus,
    }),
    delete_address: getDeleteDocumentAction(tfDefault, {
      deletionTranslationBase: 'addressDeletion',
      doctype: 'Address',
      refreshAction: 'reloadDoc',
      statusTranslationBase: 'addressStatus',
      updateDisabledStatus: updateAddressDisabledStatus,
    }),
  }
}

/**
 * A function that defines and returns a mapping of entity keys to their associated action configurations.
 * Each entry in the map specifies how to handle the creation of a new entity in the system,
 * including the type of action to be performed (e.g., modal, redirect, or slide-in), optional components,
 * titles, and route paths.
 *
 * @function getFrappeInterceptNewCreateActionMap
 * @returns {Record<string, ActionMapEntry>} A record mapping entity keys to their respective creation action configurations.
 *
 * @remarks
 * This function uses predefined relation types, components, and routes to assign actions for each supported entity.
 * It leverages a default translation function (`translationFunctionDefault`) for providing localized titles for modals.
 */
/* eslint-disable-next-line max-lines-per-function */
export const getFrappeInterceptNewCreateActionMap = (): Record<string, ActionMapEntry> => {
  const tfDefault = translationFunctionDefault()

  return {
    account: {
      route: 'new-account',
      type: 'redirect',
    },
    account_currency: {
      route: 'new-currency',
      type: 'redirect',
    },
    account_head: {
      route: 'new-account',
      type: 'redirect',
    },
    accounting_module: {
      route: 'new-email-account',
      type: 'redirect',
    },
    ...getBankAccountActions(tfDefault),
    ...getAddressActions(tfDefault),
    accumulated_depreciation_account: {
      route: 'new-account',
      type: 'redirect',
    },
    activity_type: {
      route: 'new-activity-type',
      type: 'redirect',
    },
    allocated_to: {
      route: 'new-user',
      type: 'redirect',
    },
    asset_received_but_not_billed: {
      route: 'new-account',
      type: 'redirect',
    },
    assigned_by: {
      route: 'new-user',
      type: 'redirect',
    },
    bank_account: {
      component: BankAccountCreateModal,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.bankAccountCreation.bankAccountModal.title',
      ),
      type: 'modal',
    },
    billing_address: {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.billingAddressModal.title',
      ),
      type: 'modal',
    },
    'btn-address': {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.contactAddressModal.title',
      ),
      type: 'modal',
    },
    buying_module: {
      route: 'new-email-account',
      type: 'redirect',
    },
    buying_price_list: {
      route: 'new-price-list',
      type: 'redirect',
    },
    campaign: {
      route: 'new-marketing-campaign',
      type: 'redirect',
    },
    capital_work_in_progress_account: {
      route: 'new-account',
      type: 'redirect',
    },
    confirmation_email_template: {
      route: 'new-email-template',
      type: 'redirect',
    },
    connected_app: {
      route: 'new-connected-app',
      type: 'redirect',
    },
    connected_user: {
      route: 'new-user',
      type: 'redirect',
    },
    contact: {
      ...doctypeCreateSlideInMap.Contact,
      type: 'createRelationSlideIn',
    },
    contact_person: {
      ...doctypeCreateSlideInMap.Contact,
      type: 'createRelationSlideIn',
    },
    cost_center: {
      route: 'new-cost-center',
      type: 'redirect',
    },
    create_bank_account: {
      component: BankAccountCreateModal,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.bankAccountCreation.bankAccountModal.title',
      ),
      type: 'modal',
    },
    credit_to: {
      route: 'new-account',
      type: 'redirect',
    },
    currency: {
      route: 'new-currency',
      type: 'redirect',
    },
    custom_employee: {
      route: 'new-employee',
      type: 'redirect',
    },
    custom_tw_responsible_employee: {
      route: 'new-employee',
      type: 'redirect',
    },
    customer: {
      ...doctypeCreateSlideInMap.Customer,
      type: 'createRelationSlideIn',
    },
    customer_address: {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.customerAddressModal.title',
      ),
      type: 'modal',
    },
    debit_to: {
      route: 'new-account',
      type: 'redirect',
    },
    default_advance_paid_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_advance_received_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_bank_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_buying_terms: {
      route: 'new-terms-and-conditions',
      type: 'redirect',
    },
    default_cash_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_deferred_expense_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_deferred_revenue_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_discount_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_expense_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_income_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_letter_head: {
      route: 'new-letterhead',
      type: 'redirect',
    },
    default_payable_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_price_list: {
      route: 'new-price-list',
      type: 'redirect',
    },
    default_receivable_account: {
      route: 'new-account',
      type: 'redirect',
    },
    default_selling_terms: {
      route: 'new-terms-and-conditions',
      type: 'redirect',
    },
    delivery_note_no: {
      route: 'new-delivery-note',
      type: 'redirect',
    },
    depreciation_cost_center: {
      route: 'new-cost-center',
      type: 'redirect',
    },
    depreciation_expense_account: {
      route: 'new-account',
      type: 'redirect',
    },
    disposal_account: {
      route: 'new-account',
      type: 'redirect',
    },
    domain: {
      route: 'new-email-domain',
      type: 'redirect',
    },
    edit_address: {
      component: SingleAddress,
      title: tfDefault('frappeInterceptNewCreateActionMap.addressUpdate.contactAddressModal.title'),
      type: 'modal',
    },
    email_account: {
      route: 'new-email-account',
      type: 'redirect',
    },
    email_group: {
      route: 'new-distribution-list',
      type: 'redirect',
    },
    email_template: {
      route: 'new-email-template',
      type: 'redirect',
    },
    exchange_gain_loss_account: {
      route: 'new-account',
      type: 'redirect',
    },
    expense_account: {
      route: 'new-account',
      type: 'redirect',
    },
    expenses_included_in_asset_valuation: {
      route: 'new-account',
      type: 'redirect',
    },
    for_price_list: {
      route: 'new-price-list',
      type: 'redirect',
    },
    from_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    general_module: {
      route: 'new-email-account',
      type: 'redirect',
    },
    hr_module: {
      route: 'new-email-account',
      type: 'redirect',
    },
    include_uom: {
      route: 'new-uom',
      type: 'redirect',
    },
    income_account: {
      route: 'new-account',
      type: 'redirect',
    },
    item_code: {
      route: 'new-item',
      type: 'redirect',
    },
    item_group: {
      route: 'new-item-group',
      type: 'redirect',
    },
    letter_head: {
      route: 'new-letterhead',
      type: 'redirect',
    },
    letterhead: {
      route: 'new-letterhead',
      type: 'redirect',
    },
    lost_reason: {
      route: 'new-quotation-lost-reason',
      type: 'redirect',
    },
    lost_reasons: {
      route: 'new-quotation-lost-reason',
      type: 'redirect',
    },
    opportunity: {
      route: 'new-deal',
      type: 'redirect',
    },
    organization: {
      ...doctypeCreateSlideInMap.Customer,
      type: 'createRelationSlideIn',
    },
    parent_cost_center: {
      route: 'new-cost-center',
      type: 'redirect',
    },
    parent_customer_group: {
      route: 'new-customer-group',
      type: 'redirect',
    },
    parent_item_group: {
      route: 'new-item-group',
      type: 'redirect',
    },
    parent_supplier_group: {
      route: 'new-supplier-group',
      type: 'redirect',
    },
    parent_task: {
      route: 'new-task',
      type: 'redirect',
    },
    parent_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    party_bank_account: {
      component: BankAccountCreateModal,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.bankAccountCreation.bankAccountModal.title',
      ),
      type: 'modal',
    },
    party_name: {
      ...doctypeCreateSlideInMap.Customer,
      type: 'createRelationSlideIn',
    },
    payment_term: {
      route: 'new-payment-term',
      type: 'redirect',
    },
    payment_terms: {
      route: 'new-payment-term',
      type: 'redirect',
    },
    payment_terms_template: {
      route: 'new-payment-terms-template',
      type: 'redirect',
    },
    price_list: {
      route: 'new-price-list',
      type: 'redirect',
    },
    print_format: {
      route: 'new-print-format',
      type: 'redirect',
    },
    project: {
      route: 'new-project',
      type: 'redirect',
    },
    project_module: {
      route: 'new-email-account',
      type: 'redirect',
    },
    project_name: {
      route: 'new-project',
      type: 'redirect',
    },
    provisional_expense_account: {
      route: 'new-account',
      type: 'redirect',
    },
    purchase_order: {
      route: 'new-purchase-order',
      type: 'redirect',
    },
    purchase_receipt_no: {
      route: 'new-purchase-receipt',
      type: 'redirect',
    },
    purchase_uom: {
      route: 'new-uom',
      type: 'redirect',
    },
    reference_document: {
      type: 'ref-redirect',
    },
    referral_sales_partner: {
      ...doctypeCreateSlideInMap['Sales Partner'],
      type: 'createRelationSlideIn',
    },
    rejected_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    relationship_module: {
      route: 'new-email-account',
      type: 'redirect',
    },
    reports_to: {
      route: 'new-employee',
      type: 'redirect',
    },
    round_off_account: {
      route: 'new-account',
      type: 'redirect',
    },
    round_off_cost_center: {
      route: 'new-cost-center',
      type: 'redirect',
    },
    round_off_for_opening: {
      route: 'new-account',
      type: 'redirect',
    },
    s_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    salary_currency: {
      route: 'new-currency',
      type: 'redirect',
    },
    sales_invoice_no: {
      route: 'new-sales-invoice',
      type: 'redirect',
    },
    sales_module: {
      route: 'new-email-account',
      type: 'redirect',
    },
    sales_order: {
      route: 'new-sales-order',
      type: 'redirect',
    },
    sales_partner: {
      ...doctypeCreateSlideInMap['Sales Partner'],
      type: 'createRelationSlideIn',
    },
    sales_uom: {
      route: 'new-uom',
      type: 'redirect',
    },
    salutation: {
      route: 'new-salutation',
      type: 'redirect',
    },
    selling_price_list: {
      route: 'new-price-list',
      type: 'redirect',
    },
    sender: {
      route: 'new-email-account',
      type: 'redirect',
    },
    serial_no: {
      route: 'new-serial-no',
      type: 'redirect',
    },
    series_for_depreciation_entry: {
      route: 'new-account',
      type: 'redirect',
    },
    set_from_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    set_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    shipping_address: {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.shippingAddressModal.title',
      ),
      type: 'modal',
    },
    shipping_address_name: {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.shippingAddressModal.title',
      ),
      type: 'modal',
    },
    slack_webhook_url: {
      route: 'new-slack',
      type: 'redirect',
    },
    source_warehouse_address: {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.warehouseAddressModal.title',
      ),
      type: 'modal',
    },
    stock_uom: {
      route: 'new-uom',
      type: 'redirect',
    },
    supplier: {
      ...doctypeCreateSlideInMap.Supplier,
      type: 'createRelationSlideIn',
    },
    supplier_address: {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.shippingAddressModal.title',
      ),
      type: 'modal',
    },
    supplier_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    t_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    target_warehouse_address: {
      component: SingleAddress,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.addressCreation.warehouseAddressModal.title',
      ),
      type: 'modal',
    },
    task: {
      route: 'new-task',
      type: 'redirect',
    },
    tc_name: {
      route: 'new-terms-and-conditions',
      type: 'redirect',
    },
    template: {
      route: 'new-email-template',
      type: 'redirect',
    },
    to_warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    tw_designation: {
      route: 'new-role',
      type: 'redirect',
    },
    tw_direct_delivery_customer: {
      ...doctypeCreateSlideInMap.Customer,
      type: 'createRelationSlideIn',
    },
    tw_item: {
      route: 'new-item',
      type: 'redirect',
    },
    tw_responsible_employee: {
      route: 'new-employee',
      type: 'redirect',
    },
    unrealized_exchange_gain_loss_account: {
      route: 'new-account',
      type: 'redirect',
    },
    unrealized_profit_loss_account: {
      route: 'new-account',
      type: 'redirect',
    },
    uom: {
      route: 'new-uom',
      type: 'redirect',
    },
    update_bank_account: {
      component: BankAccountCreateModal,
      title: tfDefault(
        'frappeInterceptNewCreateActionMap.bankAccountCreation.bankAccountModal.title',
      ),
      type: 'modal',
    },
    user_id: {
      route: 'new-user',
      type: 'redirect',
    },
    warehouse: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    warehouse_group: {
      route: 'new-warehouse',
      type: 'redirect',
    },
    weight_uom: {
      route: 'new-uom',
      type: 'redirect',
    },
    welcome_email_template: {
      route: 'new-email-template',
      type: 'redirect',
    },
    write_off_account: {
      route: 'new-account',
      type: 'redirect',
    },
  } satisfies Record<string, ActionMapEntry>
}
